import Link from 'next/link';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { CallToActionKind } from '@/types';
import { createPagination, getUrl } from '@/utils/paginationUtils';
import styles from './Pagination.module.scss';

export const Pagination = ({
	t,
	categ,
	nbTotalRows,
	page = 1,
	pageSize,
	filterQuery,
	onChange,
	isMobile,
	customClassName = '',
}) => {
	const currentCateg = useMemo(() => categ, [categ]);
	const [currentPage, setCurrentPage] = useState(page);

	useEffect(() => {
		setCurrentPage(page);
	}, [page]);

	let previousDisable = false;
	let nextDisable = false;

	const pageNb = Math.ceil(nbTotalRows / pageSize);
	const pagination = createPagination(currentPage, pageNb);
	// biome-ignore lint/suspicious/noAssignInExpressions: intentional side-effect in ternary
	const previousPage = parseInt(currentPage) !== 1 ? parseInt(currentPage) - 1 : (previousDisable = true);
	const previousText = t('previous');
	// biome-ignore lint/suspicious/noAssignInExpressions: intentional side-effect in ternary
	const nextPage = parseInt(currentPage) !== parseInt(pageNb) ? parseInt(currentPage) + 1 : (nextDisable = true);
	const nextText = t('next');

	const paginate = (evt, page) => {
		evt.preventDefault();
		if (page !== currentPage) {
			setCurrentPage(page);
			onChange(page);
		}
	};

	const createLinksList = (p, index) => {
		const categLink =
			p === currentPage ? (
				p
			) : currentCateg.groupIdentifier ? (
				<Link
					onClick={(e) => paginate(e, p)}
					href={getUrl(currentCateg, p, filterQuery)}
					as={getUrl(currentCateg, p, filterQuery, true)}
				>
					{p}
				</Link>
			) : (
				<a href={getUrl(currentCateg, p, filterQuery, true)} onClick={(e) => paginate(e, p)}>
					{p}
				</a>
			);

		return (
			<li key={index} className={`${styles['page-item']} ${currentPage === p ? styles.active : ''}`}>
				{p === '...' ? p : currentCateg ? categLink : <a onClick={(e) => paginate(e, p)}>{p}</a>}
			</li>
		);
	};

	const paginationStyleName = [styles.container, customClassName].filter(Boolean).join(' ');

	return (
		<div className={paginationStyleName} data-test-id="pagination-container" data-rbf="pagination-container">
			<div data-test-id="previous" data-rbf="previous">
				<CallToActionContainer
					kind={CallToActionKind.TERTIARY}
					aria-label={previousText}
					onClick={(e) => paginate(e, previousPage)}
					disabled={previousDisable}
					className={styles['left-icon']}
				>
					{isMobile ? '' : previousText}
				</CallToActionContainer>
			</div>
			<ul className={styles['inline-list']} data-test-id="list" data-rbf="list">
				{pagination?.map(createLinksList)}
			</ul>
			<div data-test-id="next" data-rbf="next">
				<CallToActionContainer
					kind={CallToActionKind.TERTIARY}
					aria-label={nextText}
					onClick={(e) => paginate(e, nextPage)}
					disabled={nextDisable}
					className={styles['right-icon']}
				>
					{isMobile ? '' : nextText}
				</CallToActionContainer>
			</div>
		</div>
	);
};

Pagination.propTypes = {
	categ: PropTypes.object.isRequired,
	nbTotalRows: PropTypes.number.isRequired,
	page: PropTypes.number,
	pageSize: PropTypes.number.isRequired,
	filterQuery: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	isMobile: PropTypes.bool.isRequired,
	t: PropTypes.func.isRequired,
	customClassName: PropTypes.string,
};

export default Pagination;
